import React, { useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EditIcon from "@mui/icons-material/Edit";

// Mock data for students with generated photos
const initialStudents = [
    {
        id: 1,
        name: "Ananya Sharma",
        major: "LLM in International Law",
        testimonial:
            "Pursuing my LLM at this university has been a deeply enriching experience. The faculty members bring unparalleled global insights to the classroom, blending academic rigor with real-world relevance. The university fosters a stimulating intellectual environment, and the exposure to international legal perspectives has broadened my thinking. From advanced seminars to impactful research opportunities, every aspect of this program has prepared me to contribute meaningfully to the global legal community. I feel truly empowered by the education I’ve received here.",
        imageUrl: "https://randomuser.me/api/portraits/women/68.jpg",
    },
    {
        id: 2,
        name: "James Okwiri",
        major: "LLM in Human Rights Law",
        testimonial:
            "The LLM program here has exceeded every expectation. The university's commitment to justice, equity, and human rights is embedded in every lecture, every case study, and every discussion. The mentorship I've received from leading scholars has been transformative—not only academically but also personally. The diverse student body and global exposure have made me a more thoughtful and effective advocate for human rights. This institution truly stands at the forefront of legal education.",
        imageUrl: "https://randomuser.me/api/portraits/men/33.jpg",
    },
    {
        id: 3,
        name: "Isabella De Luca",
        major: "LLM in Corporate and Commercial Law",
        testimonial:
            "Studying at this university has been a cornerstone in my legal journey. The LLM in Corporate and Commercial Law program offered deep insights into complex global markets, mergers, and corporate governance. The faculty are not just educators—they are leaders in their fields, bringing the latest developments and thought leadership into every class. I’ve developed not only legal expertise but also critical thinking and leadership skills. This university has given me the tools and confidence to thrive in a competitive legal landscape.",
        imageUrl: "https://randomuser.me/api/portraits/women/50.jpg",
    },
];

const Testimonials = () => {
    const [students, setStudents] = useState(initialStudents);
    const [searchTerm, setSearchTerm] = useState("");
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [studentToDelete, setStudentToDelete] = useState(null);

    // Search handler
    const handleSearch = (event) => {
        const term = event.target.value.toLowerCase();
        setSearchTerm(term);

        const filtered = initialStudents.filter(
            (student) =>
                student.name.toLowerCase().includes(term) ||
                student.major.toLowerCase().includes(term) ||
                student.testimonial.toLowerCase().includes(term)
        );

        setStudents(filtered);
    };

    // Delete confirmation handler
    const handleDeleteConfirmOpen = (student) => {
        setStudentToDelete(student);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirmClose = () => {
        setDeleteConfirmOpen(false);
        setStudentToDelete(null);
    };

    const handleDelete = () => {
        if (studentToDelete) {
            setStudents(
                students.filter((student) => student.id !== studentToDelete.id)
            );
            handleDeleteConfirmClose();
        }
    };

    return (
        <div className="container mx-auto p-6 bg-white">
            {/* Header with Search and Actions */}
            <div className="flex justify-between items-center mb-8">
                <div className="flex items-center space-x-4 w-full max-w-md relative">
                    <div className="absolute left-7 text-[#4880ff] z-10">
                        <SearchIcon />
                    </div>
                    <input
                        type="text"
                        placeholder="Search testimonials"
                        value={searchTerm}
                        onChange={handleSearch}
                        className="w-full pl-10 pr-4 py-2 border rounded-full border-[#4880ff]/30 focus:outline-none focus:ring-2 focus:ring-[#4880ff]/50"
                    />
                </div>
                <div className="flex space-x-4">
                    <button className="flex items-center px-4 py-2 bg-[#4880ff] text-white rounded-full hover:bg-[#3a6acd] transition-colors">
                        <AddIcon className="mr-2" />
                        New Testimonial
                    </button>
                    <button className="flex items-center px-4 py-2 border border-[#4880ff] text-[#4880ff] rounded-full hover:bg-[#4880ff]/10 transition-colors">
                        <CloudUploadIcon className="mr-2" />
                        Bulk Upload
                    </button>
                </div>
            </div>

            {/* Separator */}
            <div className="border-t border-gray-200 my-6"></div>

            {/* Testimonial Cards */}
            <div className="space-y-6">
                {students.map((student) => (
                    <div
                        key={student.id}
                        className="flex items-center bg-gray-50 rounded-lg shadow-sm overflow-hidden"
                    >
                        {/* Testimonial Text */}
                        <div className="w-2/3 p-6 relative">
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">
                                {student.name}
                            </h2>
                            <h3 className="text-lg text-[#4880ff] mb-4">
                                {student.major}
                            </h3>
                            <p className="text-gray-600 text-base">
                                "{student.testimonial}"
                            </p>
                            <button
                                // onClick={() => handleDeleteConfirmOpen(student)}
                                className="absolute top-0 right-[45px] bg-blue-500 text-white rounded-full h-[32px] w-[32px] flex items-center justify-center hover:bg-blue-600 transition-colors"
                            >
                                <EditIcon fontSize="small" />
                            </button>
                            <button
                                onClick={() => handleDeleteConfirmOpen(student)}
                                className="absolute top-0 right-0 bg-red-500 text-white rounded-full h-[32px] w-[32px] flex items-center justify-center hover:bg-red-600 transition-colors"
                            >
                                <DeleteIcon fontSize="small" />
                            </button>
                        </div>

                        {/* Student Image */}
                        <div className="w-1/3 flex items-center justify-center p-4">
                            <div className="relative">
                                <img
                                    src={student.imageUrl}
                                    alt={student.name}
                                    className="w-48 h-48 object-cover rounded-lg shadow-md"
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Delete Confirmation Dialog */}
            {deleteConfirmOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 shadow-xl">
                        <h2 className="text-xl font-bold mb-4">
                            Confirm Deletion
                        </h2>
                        <p className="mb-6">
                            Are you sure you want to delete the testimonial for{" "}
                            {studentToDelete?.name}?
                        </p>
                        <div className="flex justify-end space-x-4">
                            <button
                                onClick={handleDeleteConfirmClose}
                                className="px-4 py-2 border border-gray-300 rounded-full hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Testimonials;
